import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import api from "../../api";
import "./SkillTree.css";

const LEVEL_MILESTONES = [
  { level: 50, points: 1 },
  { level: 100, points: 1 },
  { level: 200, points: 2 },
  { level: 350, points: 2 },
  { level: 500, points: 2 },
  { level: 750, points: 3 },
  { level: 1000, points: 3},
  { level: 1500, points: 4 },
  { level: 2000, points: 5},
];

const CATEGORIES = [
  { key: "catcher", label: "Catcher", enabled: true },
  { key: "mining", label: "Mining", enabled: true },
  { key: "trainer", label: "Trainer", enabled: false },
  { key: "farming", label: "Farming", enabled: true },
  { key: "fishing", label: "Fishing", enabled: true },
];

const CATCHING_TREE = {
  key: "catcher",
  label: "Catcher",
  nodes: [
    {
      id: "catching_core",
      tier: 0,
      title: "Catching Mastery",
      cost: 0,
      x: 426,
      y: 70,
      effects: ["Unlocks the Catching skill tree"],
      requires: [],
    },
    {
      id: "catching_capture_t1",
      tier: 1,
      title: "Capture Efficiency I",
      cost: 1,
      x: 170,
      y: 170,
      effects: ["+5% Catch Rate"],
      requires: ["catching_core"],
    },
    {
      id: "catching_capture_t2",
      tier: 2,
      title: "Capture Efficiency II",
      cost: 2,
      x: 120,
      y: 300,
      effects: ["-10% Pokémon Flee Chance"],
      requires: ["catching_capture_t1"],
    },
    {
      id: "catching_capture_t3",
      tier: 3,
      title: "Capture Efficiency III",
      cost: 3,
      x: 170,
      y: 430,
      effects: ["+15% Catch Rate on weakened Pokémon"],
      requires: ["catching_capture_t2"],
    },
    {
      id: "catching_rare_t1",
      tier: 1,
      title: "Rare Encounters I",
      cost: 1,
      x: 426,
      y: 190,
      effects: ["+5% Shiny Odds"],
      requires: ["catching_core"],
    },
    {
      id: "catching_rare_t2",
      tier: 2,
      title: "Rare Encounters II",
      cost: 2,
      x: 426,
      y: 320,
      effects: ["+20% Spawn Rate"],
      requires: ["catching_rare_t1"],
    },
    {
      id: "catching_rare_t3",
      tier: 3,
      title: "Rare Encounters III",
      cost: 4,
      x: 426,
      y: 450,
      effects: ["Roaming Legendary Pokémon can appear"],
      requires: ["catching_rare_t2"],
    },
    {
      id: "catching_econ_t1",
      tier: 1,
      title: "Catching Economy I",
      cost: 1,
      x: 680,
      y: 170,
      effects: ["Poké Balls cost 10% less"],
      requires: ["catching_core"],
    },
    {
      id: "catching_econ_t2",
      tier: 2,
      title: "Catching Economy II",
      cost: 2,
      x: 730,
      y: 300,
      effects: ["Poké Balls cost 20% less"],
      requires: ["catching_econ_t1"],
    },
    {
      id: "catching_econ_t3",
      tier: 3,
      title: "Catching Economy III",
      cost: 3,
      x: 680,
      y: 430,
      effects: ["Master Balls become purchasable", "+1 coin per catch"],
      requires: ["catching_econ_t2"],
    },
  ],
};

const FARMING_TREE = {
  key: "farming",
  label: "Farming",
  nodes: [
    {
      id: "farming_core",
      tier: 0,
      title: "Farming Mastery",
      cost: 0,
      x: 426,
      y: 70,
      effects: ["Unlocks the Farming skill tree"],
      requires: [],
    },

    {
      id: "farming_harvest_t1",
      tier: 1,
      title: "Plentiful Harvest I",
      cost: 1,
      x: 170,
      y: 170,
      effects: ["Increase quantity gained by 10%"],
      requires: ["farming_core"],
    },
    {
      id: "farming_harvest_t2",
      tier: 2,
      title: "Plentiful Harvest II",
      cost: 2,
      x: 120,
      y: 300,
      effects: ["Increase quantity gained by 20%"],
      requires: ["farming_harvest_t1"],
    },
    {
      id: "farming_harvest_t3",
      tier: 3,
      title: "Plentiful Harvest III",
      cost: 3,
      x: 170,
      y: 430,
      effects: ["Increase quantity gained by 30%"],
      requires: ["farming_harvest_t2"],
    },

    {
      id: "farming_growth_t1",
      tier: 1,
      title: "Growing Trees I",
      cost: 1,
      x: 426,
      y: 190,
      effects: ["Growing is 10% faster"],
      requires: ["farming_core"],
    },
    {
      id: "farming_growth_t2",
      tier: 2,
      title: "Growing Trees II",
      cost: 2,
      x: 426,
      y: 320,
      effects: ["Growing is 20% faster"],
      requires: ["farming_growth_t1"],
    },
    {
      id: "farming_growth_t3",
      tier: 3,
      title: "Growing Trees III",
      cost: 3,
      x: 426,
      y: 450,
      effects: ["Growing is 30% faster"],
      requires: ["farming_growth_t2"],
    },

    {
      id: "farming_econ_t1",
      tier: 1,
      title: "Farming Economy I",
      cost: 1,
      x: 680,
      y: 170,
      effects: ["10% increase in berry selling"],
      requires: ["farming_core"],
    },
    {
      id: "farming_econ_t2",
      tier: 2,
      title: "Farming Economy II",
      cost: 2,
      x: 730,
      y: 300,
      effects: ["20% increase in berry selling"],
      requires: ["farming_econ_t1"],
    },
    {
      id: "farming_econ_t3",
      tier: 3,
      title: "Farming Economy III",
      cost: 3,
      x: 680,
      y: 430,
      effects: [
        "30% increase in berry selling",
        "Berry Shop costs 25% less",
      ],
      requires: ["farming_econ_t2"],
    },
  ],
};

const FISHING_TREE = {
  key: "fishing",
  label: "Fishing",
  nodes: [
    {
      id: "fishing_core",
      tier: 0,
      title: "Fishing Mastery",
      cost: 0,
      x: 426,
      y: 70,
      effects: ["Unlocks the Fishing skill tree"],
      requires: [],
    },

    {
      id: "fishing_rod_eff_t1",
      tier: 1,
      title: "Rod Efficiency I",
      cost: 1,
      x: 170,
      y: 170,
      effects: ["Increase Rod durability by 10%"],
      requires: ["fishing_core"],
    },
    {
      id: "fishing_rod_eff_t2",
      tier: 2,
      title: "Rod Efficiency II",
      cost: 2,
      x: 120,
      y: 300,
      effects: ["Increase Rod durability by 20%"],
      requires: ["fishing_rod_eff_t1"],
    },
    {
      id: "fishing_rod_eff_t3",
      tier: 3,
      title: "Rod Efficiency III",
      cost: 3,
      x: 90,
      y: 560,
      effects: ["Increase Rod durability by 30%"],
      requires: ["fishing_rod_eff_t2"],
    },

    {
      id: "fishing_rod_econ_t1",
      tier: 1,
      title: "Rod Economy I",
      cost: 1,
      x: 426,
      y: 190,
      effects: ["Rods cost 10% off", "Repairs cost 10% off"],
      requires: ["fishing_core"],
    },
    {
      id: "fishing_rod_econ_t2",
      tier: 2,
      title: "Rod Economy II",
      cost: 2,
      x: 426,
      y: 320,
      effects: ["Rods cost 20% off", "Repairs cost 20% off"],
      requires: ["fishing_rod_econ_t1"],
    },
    {
      id: "fishing_rod_econ_t3",
      tier: 3,
      title: "Rod Economy III",
      cost: 3,
      x: 426,
      y: 450,
      effects: ["Rods cost 30% off", "Repairs cost 30% off"],
      requires: ["fishing_rod_econ_t2"],
    },

    {
      id: "fishing_conv_rate_t1",
      tier: 1,
      title: "Conversion Rate I",
      cost: 1,
      x: 270,
      y: 300,
      effects: ["Fisher Coins → Coins is 10% off", "Item conversions pay out 10% more"],
      requires: ["fishing_rod_econ_t1"],
    },
    {
      id: "fishing_conv_rate_t2",
      tier: 2,
      title: "Conversion Rate II",
      cost: 2,
      x: 220,
      y: 430,
      effects: ["Fisher Coins → Coins is 20% off", "Item conversions pay out 20% more"],
      requires: ["fishing_conv_rate_t1"],
    },
    {
      id: "fishing_conv_rate_t3",
      tier: 3,
      title: "Conversion Rate III",
      cost: 3,
      x: 270,
      y: 560,
      effects: ["Fisher Coins → Coins is 30% off", "Item conversions pay out 30% more"],
      requires: ["fishing_conv_rate_t2"],
    },

    {
      id: "fishing_rod_enc_t1",
      tier: 1,
      title: "Rod Encounters I",
      cost: 1,
      x: 680,
      y: 170,
      effects: ["Enables fishing Pokémon to be shiny"],
      requires: ["fishing_core"],
    },
    {
      id: "fishing_rod_enc_t2",
      tier: 2,
      title: "Rod Encounters II",
      cost: 2,
      x: 730,
      y: 300,
      effects: ["Fishing Pokémon have a higher chance for better IVs"],
      requires: ["fishing_rod_enc_t1"],
    },
    {
      id: "fishing_rod_enc_t3",
      tier: 3,
      title: "Rod Encounters III",
      cost: 3,
      x: 680,
      y: 430,
      effects: ["Legendary water Pokémon can be spawned from fishing (low chance)"],
      requires: ["fishing_rod_enc_t2"],
    },
  ],
};

const MINING_TREE = {
  key: "mining",
  label: "Mining",
  nodes: [
    {
      id: "mining_core",
      tier: 0,
      title: "Mining Mastery",
      cost: 0,
      x: 426,
      y: 70,
      effects: ["Unlocks the Mining skill tree"],
      requires: [],
    },

    {
      id: "mining_pickaxe_t1",
      tier: 1,
      title: "Pickaxe Efficiency I",
      cost: 1,
      x: 170,
      y: 170,
      effects: ["Increase Pickaxe Duration by 10%"],
      requires: ["mining_core"],
    },
    {
      id: "mining_pickaxe_t2",
      tier: 2,
      title: "Pickaxe Efficiency II",
      cost: 2,
      x: 120,
      y: 300,
      effects: ["Increase Pickaxe Duration by 20%"],
      requires: ["mining_pickaxe_t1"],
    },
    {
      id: "mining_pickaxe_t3",
      tier: 3,
      title: "Pickaxe Efficiency III",
      cost: 3,
      x: 170,
      y: 430,
      effects: ["Increase Pickaxe Duration by 30%"],
      requires: ["mining_pickaxe_t2"],
    },

    {
      id: "mining_econ_t1",
      tier: 1,
      title: "Mining Economy I",
      cost: 1,
      x: 426,
      y: 190,
      effects: ["Pickaxe cost 10% less"],
      requires: ["mining_core"],
    },
    {
      id: "mining_econ_t2",
      tier: 2,
      title: "Mining Economy II",
      cost: 2,
      x: 426,
      y: 320,
      effects: ["Pickaxe cost 20% less"],
      requires: ["mining_econ_t1"],
    },
    {
      id: "mining_econ_t3",
      tier: 3,
      title: "Mining Economy III",
      cost: 3,
      x: 426,
      y: 450,
      effects: ["All Mining Items are 10% off", "20% increase when selling gems/rocks in mining shop"],
      requires: ["mining_econ_t2"],
    },

    {
      id: "mining_enc_t1",
      tier: 1,
      title: "Mining Encounters I",
      cost: 2,
      x: 680,
      y: 170,
      effects: ["When mining and the drop is Rock, gain 2 Rocks total"],
      requires: ["mining_core"],
    },
    {
      id: "mining_enc_t2",
      tier: 2,
      title: "Mining Encounters II",
      cost: 2,
      x: 730,
      y: 300,
      effects: ["When mining and the drop is Rock, gain 3 Rocks total"],
      requires: ["mining_enc_t1"],
    },
    {
      id: "mining_enc_t3",
      tier: 3,
      title: "Mining Encounters III",
      cost: 3,
      x: 680,
      y: 430,
      effects: ["20% rock respawn cooldown rate"],
      requires: ["mining_enc_t2"],
    },
  ],
};

const GLOBAL_SKILLTREE_NODES = [...CATCHING_TREE.nodes, ...MINING_TREE.nodes, ...FISHING_TREE.nodes, ...FARMING_TREE.nodes];

const pointsFromLevel = (level) => {
  return LEVEL_MILESTONES.reduce((sum, m) => (level >= m.level ? sum + m.points : sum), 0);
};

const getMilestoneStatus = (level) => {
  const next = LEVEL_MILESTONES.find((m) => level < m.level) || null;
  const prev = [...LEVEL_MILESTONES].reverse().find((m) => level >= m.level) || null;

  return { next, prev };
};

const CATEGORY_META = {
  trainer: {
    label: "Trainer",
    icon: "/items/battleseek.png",
    color: "#ef4444",
    bg: "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.05) 100%)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    expPower: 3,
  },
  catching: {
    label: "Catcher",
    icon: "/items/pokeball.png",
    color: "#22c55e",
    bg: "linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.05) 100%)",
    border: "1px solid rgba(34, 197, 94, 0.3)",
    expPower: 2.5,
  },
  mining: {
    label: "Mining",
    icon: "/items/NormalPickaxe.png",
    color: "#d97706",
    bg: "linear-gradient(135deg, rgba(161, 98, 7, 0.15) 0%, rgba(120, 53, 15, 0.05) 100%)",
    border: "1px solid rgba(161, 98, 7, 0.3)",
    expPower: 2.5,
  },
  fishing: {
    label: "Fishing",
    icon: "/items/superrod.png",
    color: "#3b82f6",
    bg: "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.05) 100%)",
    border: "1px solid rgba(59, 130, 246, 0.3)",
    expPower: 2.5,
  },
  farming: {
    label: "Farming",
    icon: "/items/razzberry.png",
    color: "#fb923c",
    bg: "linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(249, 115, 22, 0.05) 100%)",
    border: "1px solid rgba(251, 146, 60, 0.3)",
    expPower: 2.5,
  },
};

const expAtLevel = (level, expPower) => {
  if (!level || level < 0) return 0;
  return Math.floor(Math.pow(level, expPower));
};

const getMilestoneProgress = (key, level, exp) => {
  const status = getMilestoneStatus(level);
  const meta = CATEGORY_META[key];
  if (!meta) return { ...status, startLevel: 0, endLevel: 0, startExp: 0, endExp: 0, progress: 0, remainingExp: 0 };

  if (!status.next) {
    return {
      ...status,
      startLevel: status.prev ? status.prev.level : 0,
      endLevel: status.prev ? status.prev.level : 0,
      startExp: status.prev ? expAtLevel(status.prev.level, meta.expPower) : 0,
      endExp: status.prev ? expAtLevel(status.prev.level, meta.expPower) : 0,
      progress: 1,
      remainingExp: 0,
    };
  }

  const startLevel = status.prev ? status.prev.level : 0;
  const endLevel = status.next.level;
  const startExp = expAtLevel(startLevel, meta.expPower);
  const endExp = expAtLevel(endLevel, meta.expPower);
  const denom = Math.max(1, endExp - startExp);
  const progress = (Math.max(startExp, exp || 0) - startExp) / denom;
  const clamped = Math.min(1, Math.max(0, progress));
  const remainingExp = Math.max(0, endExp - (exp || 0));

  return { ...status, startLevel, endLevel, startExp, endExp, progress: clamped, remainingExp };
};

const getMilestoneFillPercent = (level, statusProgress, prevLevel, hasNext) => {
  const nodes = [{ level: 0, points: 0 }, ...LEVEL_MILESTONES];
  const segments = Math.max(1, nodes.length - 1);
  const segmentPct = 100 / segments;

  if (!hasNext) return 100;

  const prevIndex = prevLevel ? LEVEL_MILESTONES.findIndex((m) => m.level === prevLevel) + 1 : 0;
  const base = prevIndex * segmentPct;
  const fill = base + (statusProgress || 0) * segmentPct;
  return Math.min(100, Math.max(0, fill));
};

const SkillTree = () => {
  document.title = "Skill Tree | Pokémon Radiance";

  const [activeCategory, setActiveCategory] = useState("catcher");
  const [levels, setLevels] = useState({ trainer: 0, catching: 0, mining: 0, fishing: 0, farming: 0 });
  const [skillExp, setSkillExp] = useState({ trainer: 0, catching: 0, mining: 0, fishing: 0, farming: 0 });
  const [purchased, setPurchased] = useState(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [hoverTooltip, setHoverTooltip] = useState(null);
  const [activeView, setActiveView] = useState("tree");

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetIsLoading, setResetIsLoading] = useState(false);

  const applySkilltreeState = (data) => {
    const lv = data?.levels || {};
    const ex = data?.exp || {};
    const purchases = Array.isArray(data?.purchases) ? data.purchases : [];

    setSkillExp({
      trainer: ex?.trainer || 0,
      catching: ex?.catcher || 0,
      mining: ex?.mining || 0,
      fishing: ex?.fishing || 0,
      farming: ex?.farming || 0,
    });
    setLevels({
      trainer: lv?.trainer || 1,
      catching: lv?.catcher || 1,
      mining: lv?.mining || 1,
      fishing: lv?.fishing || 1,
      farming: lv?.farming || 1,
    });
    setPurchased(new Set(purchases));
    if (purchases.includes("catching_core") || purchases.includes("mining_core")) {
      setSelectedNodeId((prev) => (prev ? prev : "catching_core"));
    }
  };

  const refreshSkilltreeState = () => {
    api
      .get("/v1/skilltree/state", { withCredentials: true })
      .then((res) => {
        applySkilltreeState(res.data);
      })
      .catch((err) => {
        console.error("Error fetching skill tree state:", err);
      });
  };

  useEffect(() => {
    refreshSkilltreeState();
  }, []);

  const mapViewportRef = useRef(null);
  const dragStateRef = useRef({ dragging: false, pointerId: null, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const viewRef = useRef(view);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef({ active: false, baseDist: 0, baseScale: 1, baseX: 0, baseY: 0, baseMidX: 0, baseMidY: 0 });
  const [nodeRadius, setNodeRadius] = useState(24);

  const tree = useMemo(() => {
    if (activeCategory === "catcher") return CATCHING_TREE;
    if (activeCategory === "mining") return MINING_TREE;
    if (activeCategory === "fishing") return FISHING_TREE;
    if (activeCategory === "farming") return FARMING_TREE;
    return CATCHING_TREE;
  }, [activeCategory]);

  useEffect(() => {
    const rootId = (() => {
      if (activeCategory === "mining") return "mining_core";
      if (activeCategory === "fishing") return "fishing_core";
      if (activeCategory === "farming") return "farming_core";
      return "catching_core";
    })();
    const valid = tree.nodes.some((n) => n.id === selectedNodeId);
    if (!valid) setSelectedNodeId(rootId);
  }, [activeCategory, selectedNodeId, tree.nodes]);

  const totalPoints = useMemo(() => {
    return Object.values(levels).reduce((sum, lvl) => sum + pointsFromLevel(lvl), 0);
  }, [levels]);
  const spentPoints = useMemo(() => {
    const byId = new Map(GLOBAL_SKILLTREE_NODES.map((n) => [n.id, n]));
    let sum = 0;
    purchased.forEach((id) => {
      const node = byId.get(id);
      if (node) sum += node.cost;
    });
    return sum;
  }, [purchased]);

  const availablePoints = Math.max(0, totalPoints - spentPoints);

  const milestoneRows = useMemo(() => {
    return [
      { key: "trainer", level: levels.trainer },
      { key: "catching", level: levels.catching },
      { key: "mining", level: levels.mining },
      { key: "fishing", level: levels.fishing },
      { key: "farming", level: levels.farming },
    ].map((row) => {
      const meta = CATEGORY_META[row.key];
      const earned = pointsFromLevel(row.level);
      const status = getMilestoneProgress(row.key, row.level, skillExp[row.key]);
      return { ...row, meta, earned, status, exp: skillExp[row.key] };
    });
  }, [levels, skillExp]);

  const nodeById = useMemo(() => new Map(tree.nodes.map((n) => [n.id, n])), [tree.nodes]);

  const activeDetailNode = nodeById.get(hoveredNodeId || selectedNodeId) || tree.nodes[0];
  const hoveredNode = hoveredNodeId ? nodeById.get(hoveredNodeId) : null;

  const activeDetailState = useMemo(() => {
    if (!activeDetailNode) return "locked";
    if (purchased.has(activeDetailNode.id)) return "completed";
    const reqOk = (activeDetailNode.requires || []).every((r) => purchased.has(r));
    if (!reqOk) return "locked";
    if (availablePoints >= activeDetailNode.cost) return "unlocked";
    return "unavailable";
  }, [activeDetailNode, availablePoints, purchased]);

  const canUnlock = (node) => {
    const reqOk = (node.requires || []).every((r) => purchased.has(r));
    return reqOk;
  };

  const getNodeState = (node) => {
    if (purchased.has(node.id)) return "completed";
    if (!canUnlock(node)) return "locked";
    if (availablePoints >= node.cost) return "unlocked";
    return "unavailable";
  };

  const handlePurchase = (node) => {
    const state = getNodeState(node);
    if (state !== "unlocked") return;

    api
      .post(
        "/v1/skilltree/purchase",
        { node_id: node.id },
        { withCredentials: true }
      )
      .then((res) => {
        applySkilltreeState(res.data);
      })
      .catch((err) => {
        console.error("Error purchasing skill tree node:", err);
      });
  };

  const handleResetCategory = async () => {
    setResetModalOpen(true);
  };

  const confirmResetCategory = async () => {
    const apiCategory = activeCategory === "catcher" ? "catching" : activeCategory;
    try {
      setResetIsLoading(true);
      const res = await api.post(
        "/v1/skilltree/reset-category",
        { category: apiCategory },
        { withCredentials: true }
      );
      applySkilltreeState(res.data);
      setResetModalOpen(false);
    } catch (err) {
      console.error("Error resetting skill tree category:", err);
    } finally {
      setResetIsLoading(false);
    }
  };

  const edges = useMemo(() => {
    const list = [];
    tree.nodes.forEach((node) => {
      (node.requires || []).forEach((req) => {
        const from = nodeById.get(req);
        const to = node;
        if (from && to) list.push({ from: from.id, to: to.id });
      });
    });
    return list;
  }, [tree.nodes, nodeById]);

  const selectedPath = useMemo(() => {
    if (!selectedNodeId) return new Set();
    const visited = new Set();
    const stack = [selectedNodeId];
    while (stack.length) {
      const id = stack.pop();
      if (!id || visited.has(id)) continue;
      visited.add(id);
      const node = nodeById.get(id);
      (node?.requires || []).forEach((req) => stack.push(req));
    }
    return visited;
  }, [nodeById, selectedNodeId]);

  const selectedPathEdges = useMemo(() => {
    if (!selectedNodeId) return new Set();
    const set = new Set();
    edges.forEach((edge) => {
      if (selectedPath.has(edge.from) && selectedPath.has(edge.to)) {
        set.add(`${edge.from}_${edge.to}`);
      }
    });
    return set;
  }, [edges, selectedNodeId, selectedPath]);

  const getEdgeState = (fromId, toId) => {
    if (purchased.has(toId)) return "completed";
    if (purchased.has(fromId) && canUnlock(nodeById.get(toId))) return "unlocked";
    return "locked";
  };

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  useEffect(() => {
    const el = mapViewportRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      setNodeRadius(rect.width < 576 ? 22 : 27);
    };

    update();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const handleViewportPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (e.target && typeof e.target.closest === "function") {
      if (e.target.closest(".skill-tree-map-controls") || e.target.closest(".skill-tree-node")) return;
    }
    const el = mapViewportRef.current;
    if (el && e.pointerId != null) {
      try {
        el.setPointerCapture(e.pointerId);
      } catch (err) {
        // ignore
      }
    }

    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pointers = Array.from(pointersRef.current.values());

    if (pointers.length === 2) {
      const [p1, p2] = pointers;
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const rect = el?.getBoundingClientRect();
      const midX = rect ? (p1.x + p2.x) / 2 - rect.left : 0;
      const midY = rect ? (p1.y + p2.y) / 2 - rect.top : 0;
      pinchRef.current = {
        active: true,
        baseDist: dist,
        baseScale: viewRef.current.scale,
        baseX: viewRef.current.x,
        baseY: viewRef.current.y,
        baseMidX: midX,
        baseMidY: midY,
      };
      dragStateRef.current.dragging = true;
      dragStateRef.current.pointerId = null;
      return;
    }

    pinchRef.current.active = false;
    dragStateRef.current = {
      dragging: true,
      pointerId: e.pointerId ?? null,
      startX: e.clientX,
      startY: e.clientY,
      baseX: viewRef.current.x,
      baseY: viewRef.current.y,
    };
  };

  const handleViewportPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const el = mapViewportRef.current;
    const pointers = Array.from(pointersRef.current.values());
    if (pinchRef.current.active && pointers.length === 2 && el) {
      const [p1, p2] = pointers;
      const rect = el.getBoundingClientRect();
      const midX = (p1.x + p2.x) / 2 - rect.left;
      const midY = (p1.y + p2.y) / 2 - rect.top;
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      const ratio = pinchRef.current.baseDist ? dist / pinchRef.current.baseDist : 1;
      const nextScale = clamp(pinchRef.current.baseScale * ratio, 0.45, 1.5);

      const worldX = (pinchRef.current.baseMidX - pinchRef.current.baseX) / pinchRef.current.baseScale;
      const worldY = (pinchRef.current.baseMidY - pinchRef.current.baseY) / pinchRef.current.baseScale;
      const nx = midX - worldX * nextScale;
      const ny = midY - worldY * nextScale;
      setView({ x: nx, y: ny, scale: nextScale });
      return;
    }

    if (!dragStateRef.current.dragging) return;
    if (dragStateRef.current.pointerId != null && e.pointerId !== dragStateRef.current.pointerId) return;
    const dx = e.clientX - dragStateRef.current.startX;
    const dy = e.clientY - dragStateRef.current.startY;
    setView((v) => ({ ...v, x: dragStateRef.current.baseX + dx, y: dragStateRef.current.baseY + dy }));
  };

  const handleViewportPointerUp = (e) => {
    const el = mapViewportRef.current;
    if (el && dragStateRef.current.pointerId != null) {
      try {
        el.releasePointerCapture(dragStateRef.current.pointerId);
      } catch (err) {
        // ignore
      }
    }

    pointersRef.current.delete(e.pointerId);

    const remaining = Array.from(pointersRef.current.entries());
    if (remaining.length === 2) {
      // Still pinching.
      return;
    }

    if (remaining.length === 1) {
      const [pointerId, p] = remaining[0];
      pinchRef.current.active = false;
      dragStateRef.current = {
        dragging: true,
        pointerId,
        startX: p.x,
        startY: p.y,
        baseX: viewRef.current.x,
        baseY: viewRef.current.y,
      };
      return;
    }

    pinchRef.current.active = false;
    dragStateRef.current.dragging = false;
    dragStateRef.current.pointerId = null;
  };

  const handleViewportWheel = useCallback((e) => {
    e.preventDefault();
    const cur = viewRef.current;
    const nextScale = Math.min(1.5, Math.max(0.45, cur.scale + (e.deltaY > 0 ? -0.08 : 0.08)));

    const el = mapViewportRef.current;
    if (!el) {
      setView((v) => ({ ...v, scale: nextScale }));
      return;
    }

    const rect = el.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    setView((v) => {
      const sx = (mx - v.x) / v.scale;
      const sy = (my - v.y) / v.scale;
      const nx = mx - sx * nextScale;
      const ny = my - sy * nextScale;
      return { x: nx, y: ny, scale: nextScale };
    });
  }, []);

  const showTooltip = (node, e) => {
    const el = mapViewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setHoverTooltip({
      id: node.id,
      x: e.clientX - rect.left + 14,
      y: e.clientY - rect.top + 14,
    });
  };

  const centerOnRoot = (scaleOverride) => {
    const el = mapViewportRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const root = tree.nodes.find((n) => n.tier === 0) || tree.nodes[0];
    if (!root) return;

    setView((v) => {
      const scale = scaleOverride != null ? scaleOverride : rect.width < 520 ? 0.85 : 1;
      const nx = rect.width / 2 - ((root.x || 0) + nodeRadius) * scale;
      const ny = rect.height / 2 - ((root.y || 0) + nodeRadius) * scale;
      return { ...v, x: nx, y: ny, scale };
    });
  };

  const zoomBy = (delta) => {
    const el = mapViewportRef.current;
    if (!el) {
      setView((v) => ({ ...v, scale: clamp(v.scale + delta, 0.45, 1.5) }));
      return;
    }
    const rect = el.getBoundingClientRect();
    const mx = rect.width / 2;
    const my = rect.height / 2;
    setView((v) => {
      const nextScale = clamp(v.scale + delta, 0.45, 1.5);
      const sx = (mx - v.x) / v.scale;
      const sy = (my - v.y) / v.scale;
      const nx = mx - sx * nextScale;
      const ny = my - sy * nextScale;
      return { x: nx, y: ny, scale: nextScale };
    });
  };

  useEffect(() => {
    centerOnRoot();
  }, [activeCategory, tree.nodes, nodeRadius]);

  useEffect(() => {
    const el = mapViewportRef.current;
    if (!el) return;
    const onWheel = (e) => handleViewportWheel(e);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [handleViewportWheel]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Pokemon Radiance" breadcrumbItem="Skill Tree" />

          <Row className="mb-3">
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody className="skill-tree-header">
                  <div className="skill-tree-header-left">
                    <div className="skill-tree-title">Global Skill Points</div>
                    <div className="skill-tree-subtitle">
                      Earn points from level milestones. Spend them in any skill tree.
                    </div>
                  </div>

                  <div className="skill-tree-header-right">
                    <div className="skill-tree-levels">
                      {milestoneRows.map((r) => (
                        <div
                          key={r.key}
                          className="skill-tree-level-pill"
                          style={{ background: r.meta?.bg, border: r.meta?.border }}
                        >
                          <img
                            src={r.meta?.icon}
                            alt={r.meta?.label}
                            className="skill-tree-level-icon"
                          />
                          <div className="skill-tree-level-pill-text">
                            <div className="skill-tree-level-pill-label">{r.meta?.label}</div>
                            <div className="skill-tree-level-pill-value" style={{ color: r.meta?.color }}>
                              Lv. {r.level.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="skill-tree-points">
                      <div className="skill-tree-kv">
                        <div className="skill-tree-kv-label">Earned</div>
                        <div className="skill-tree-kv-value">{totalPoints}</div>
                      </div>
                      <div className="skill-tree-kv">
                        <div className="skill-tree-kv-label">Spent</div>
                        <div className="skill-tree-kv-value">{spentPoints}</div>
                      </div>
                      <div className="skill-tree-kv skill-tree-kv-available">
                        <div className="skill-tree-kv-label">Available</div>
                        <div className="skill-tree-kv-value">{availablePoints}</div>
                      </div>
                    </div>

                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody className="skill-tree-view-tabs">
                  <button
                    type="button"
                    className={`skill-tree-view-tab ${activeView === "tree" ? "active" : ""}`}
                    onClick={() => setActiveView("tree")}
                  >
                    Skill Tree
                  </button>
                  <button
                    type="button"
                    className={`skill-tree-view-tab ${activeView === "milestones" ? "active" : ""}`}
                    onClick={() => setActiveView("milestones")}
                  >
                    Milestones
                  </button>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {activeView === "tree" && (
            <Row className="mb-3">
              <Col lg={12}>
                <Card className="shadow-sm">
                  <CardBody className="skill-tree-categories">
                    {CATEGORIES.map((c) => {
                      const isActive = c.key === activeCategory;
                      return (
                        <button
                          key={c.key}
                          type="button"
                          className={`skill-tree-category ${isActive ? "active" : ""} ${
                            c.enabled ? "" : "disabled"
                          }`}
                          onClick={() => c.enabled && setActiveCategory(c.key)}
                        >
                          <span className="skill-tree-category-label">{c.label}</span>
                          {!c.enabled && <span className="skill-tree-category-chip">Soon</span>}
                        </button>
                      );
                    })}

                    {activeCategory !== "trainer" && (
                      <button
                        type="button"
                        className="skill-tree-category"
                        style={{ marginLeft: "auto", opacity: 0.95 }}
                        onClick={handleResetCategory}
                      >
                        <span className="skill-tree-category-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          Reset (10,000
                          <img
                            src="/items/coin.png"
                            alt="Coins"
                            style={{ width: 16, height: 16, imageRendering: "pixelated" }}
                          />
                          )
                        </span>
                      </button>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {resetModalOpen && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                backdropFilter: "blur(5px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  backgroundColor: "rgba(25, 25, 35, 0.95)",
                  borderRadius: "12px",
                  width: "92%",
                  maxWidth: "520px",
                  boxShadow:
                    "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                  overflow: "hidden",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  animation: "fadeIn 0.3s ease-out",
                }}
              >
                <div
                  style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <h5 style={{ margin: 0, color: "#fff", fontWeight: "600" }}>
                    Reset Skill Tree
                  </h5>
                  <button
                    onClick={() => !resetIsLoading && setResetModalOpen(false)}
                    disabled={resetIsLoading}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#fff",
                      fontSize: "20px",
                      cursor: resetIsLoading ? "not-allowed" : "pointer",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      opacity: resetIsLoading ? 0.35 : 0.7,
                      flexShrink: 0,
                      minWidth: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onMouseEnter={(e) => {
                      if (!resetIsLoading) e.target.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      if (!resetIsLoading) e.target.style.opacity = "0.7";
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div
                  style={{
                    padding: "22px 20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    gap: 12,
                  }}
                >
                  {(() => {
                    const apiCategory = activeCategory === "catcher" ? "catching" : activeCategory;
                    const labelMeta = activeCategory === "catcher" ? CATEGORY_META.catching : CATEGORY_META[activeCategory];
                    const label = labelMeta?.label || apiCategory;
                    return (
                      <>
                        <div style={{ fontSize: "16px", fontWeight: 700 }}>
                          Reset {label} skill tree?
                        </div>
                        <div style={{ opacity: 0.9 }}>
                          This will remove all purchased nodes in this category (core stays available).
                        </div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                          Cost: <strong>10,000</strong>
                          <img
                            src="/items/coin.png"
                            alt="Coins"
                            style={{ width: 18, height: 18, imageRendering: "pixelated" }}
                          />
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                    display: "flex",
                    justifyContent: "center",
                    gap: 10,
                    background: "rgba(0, 0, 0, 0.2)",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => setResetModalOpen(false)}
                    disabled={resetIsLoading}
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      color: "#fff",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: resetIsLoading ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      opacity: resetIsLoading ? 0.6 : 1,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmResetCategory}
                    disabled={resetIsLoading}
                    style={{
                      background: "rgba(220, 53, 69, 0.25)",
                      border: "1px solid rgba(220, 53, 69, 0.5)",
                      color: "#fff",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: resetIsLoading ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: 700,
                      opacity: resetIsLoading ? 0.6 : 1,
                    }}
                  >
                    {resetIsLoading ? "Resetting..." : "Reset"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeView === "milestones" ? (
            <Row>
              <Col lg={12}>
                <Card className="shadow-sm">
                  <CardBody>
                    <div className="skill-tree-milestones-grid">
                      {milestoneRows.map((r) => {
                        const hasNext = !!r.status.next;
                        const nextLevel = r.status.next?.level;
                        const nextPoints = r.status.next?.points;
                        const levelsToGo = hasNext ? Math.max(0, nextLevel - r.level) : 0;
                        const remainingExp = r.status.remainingExp || 0;
                        const fillPercent = getMilestoneFillPercent(
                          r.level,
                          r.status.progress,
                          r.status.prev?.level,
                          hasNext
                        );
                        const timelineNodes = [{ level: 0, points: 0 }, ...LEVEL_MILESTONES];
                        return (
                          <div
                            key={r.key}
                            className="skill-tree-milestone-card"
                            style={{ background: r.meta?.bg, border: r.meta?.border }}
                          >
                            <div className="skill-tree-milestone-top">
                              <div className="skill-tree-milestone-left">
                                <img
                                  src={r.meta?.icon}
                                  alt={r.meta?.label}
                                  className="skill-tree-milestone-icon"
                                />
                                <div>
                                  <div className="skill-tree-milestone-label">{r.meta?.label}</div>
                                  <div className="skill-tree-milestone-level" style={{ color: r.meta?.color }}>
                                    Lv. {r.level.toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              <div className="skill-tree-milestone-earned">
                                <div className="skill-tree-milestone-earned-label">Earned</div>
                                <div className="skill-tree-milestone-earned-value">{r.earned} SP</div>
                              </div>
                            </div>

                            <div className="skill-tree-milestone-progress">
                              <div className="skill-tree-milestone-progress-bar">
                                <div
                                  className="skill-tree-milestone-progress-fill"
                                  style={{ width: `${Math.round(r.status.progress * 100)}%`, background: r.meta?.color }}
                                />
                              </div>
                              {hasNext ? (
                                <div className="skill-tree-milestone-progress-text">
                                  Next milestone: Lv. {nextLevel.toLocaleString()} (+{nextPoints} SP) • {levelsToGo.toLocaleString()} levels to go • {remainingExp.toLocaleString()} EXP remaining
                                </div>
                              ) : (
                                <div className="skill-tree-milestone-progress-text">
                                  All milestones completed
                                </div>
                              )}
                            </div>

                            <div className="skill-tree-milestone-timeline">
                              <div className="skill-tree-milestone-timeline-track" />
                              <div
                                className="skill-tree-milestone-timeline-track-fill"
                                style={{ width: `${fillPercent}%`, background: r.meta?.color }}
                              />
                              <div className="skill-tree-milestone-timeline-nodes">
                                {timelineNodes.map((m, idx) => {
                                  const isStart = idx === 0;
                                  const reached = r.level >= m.level;
                                  const label = isStart ? "Start" : `Lv. ${m.level.toLocaleString()}`;
                                  const points = isStart ? 0 : m.points;
                                  return (
                                    <div key={`${r.key}_${m.level}`} className="skill-tree-milestone-timeline-node">
                                      <div
                                        className={`skill-tree-milestone-circle ${reached ? "filled" : ""} ${
                                          !reached && r.status.next?.level === m.level ? "next" : ""
                                        }`}
                                        style={reached ? { borderColor: r.meta?.color, background: r.meta?.color } : { borderColor: "rgba(255,255,255,0.35)" }}
                                        title={isStart ? "Start" : `${label} (+${points} SP)`}
                                      >
                                        {!isStart && (
                                          <span className="skill-tree-milestone-circle-points">+{points}</span>
                                        )}
                                      </div>
                                      <div className="skill-tree-milestone-circle-label">{isStart ? "0" : m.level.toLocaleString()}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col lg={8}>
                <Card className="shadow-sm">
                  <CardBody>
                    <div
                      className={`skill-tree-map-viewport ${dragStateRef.current.dragging ? "dragging" : ""}`}
                      ref={mapViewportRef}
                      onPointerDown={handleViewportPointerDown}
                      onPointerMove={handleViewportPointerMove}
                      onPointerUp={handleViewportPointerUp}
                      onPointerCancel={handleViewportPointerUp}
                      onPointerLeave={handleViewportPointerUp}
                    >
                      <div className="skill-tree-map" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}>
                        <div className="skill-tree-map-bg" />
                        <svg className="skill-tree-links" width="900" height="620" viewBox="0 0 900 620">
                          {edges.map((edge) => {
                            const from = nodeById.get(edge.from);
                            const to = nodeById.get(edge.to);
                            if (!from || !to) return null;
                            const x1 = (from.x || 0) + nodeRadius;
                            const y1 = (from.y || 0) + nodeRadius;
                            const x2 = (to.x || 0) + nodeRadius;
                            const y2 = (to.y || 0) + nodeRadius;
                            const state = getEdgeState(edge.from, edge.to);
                            const edgeKey = `${edge.from}_${edge.to}`;
                            const isPath = selectedPathEdges.has(edgeKey);
                            return (
                              <line
                                key={`${edge.from}_${edge.to}`}
                                x1={x1}
                                y1={y1}
                                x2={x2}
                                y2={y2}
                                className={`skill-tree-link ${state} ${isPath ? "path" : ""}`}
                              />
                            );
                          })}
                        </svg>

                        {tree.nodes.map((node) => {
                          const state = getNodeState(node);
                          const isSelected = node.id === selectedNodeId;
                          const isOnPath = selectedPath.has(node.id);
                          return (
                            <button
                              key={node.id}
                              type="button"
                              className={`skill-tree-node ${state} ${isSelected ? "selected" : ""} ${
                                isOnPath ? "path" : ""
                              }`}
                              style={{ left: node.x, top: node.y }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onMouseEnter={(e) => {
                                setHoveredNodeId(node.id);
                                showTooltip(node, e);
                              }}
                              onMouseMove={(e) => hoveredNodeId === node.id && showTooltip(node, e)}
                              onMouseLeave={() => {
                                setHoveredNodeId(null);
                                setHoverTooltip(null);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedNodeId(node.id);
                              }}
                            >
                              <div className={`skill-tree-node-core ${state}`} />
                              <div className="skill-tree-node-ring" />
                              <div className="skill-tree-node-tier">{node.tier}</div>
                            </button>
                          );
                        })}
                      </div>

                      {hoverTooltip && hoveredNodeId && (
                        <div className="skill-tree-tooltip" style={{ left: hoverTooltip.x, top: hoverTooltip.y }}>
                          <div className="skill-tree-tooltip-title">{hoveredNode?.title}</div>
                          <div className="skill-tree-tooltip-meta">
                            Tier {hoveredNode?.tier} • Cost {hoveredNode?.cost} SP
                          </div>
                          {hoveredNode?.effects?.length ? (
                            <div className="skill-tree-tooltip-effects">
                              {hoveredNode.effects.slice(0, 2).map((effect, idx) => (
                                <div key={`${hoveredNode.id}_effect_${idx}`} className="skill-tree-tooltip-effect">
                                  {effect}
                                </div>
                              ))}
                              {hoveredNode.effects.length > 2 && (
                                <div className="skill-tree-tooltip-more">
                                  +{hoveredNode.effects.length - 2} more
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      )}

                      <div className="skill-tree-map-controls" onPointerDown={(e) => e.stopPropagation()}>
                        <button type="button" className="skill-tree-map-btn" onPointerDown={(e) => e.stopPropagation()} onClick={() => zoomBy(0.1)}>
                          +
                        </button>
                        <button type="button" className="skill-tree-map-btn" onPointerDown={(e) => e.stopPropagation()} onClick={() => zoomBy(-0.1)}>
                          -
                        </button>
                        <button type="button" className="skill-tree-map-btn" onPointerDown={(e) => e.stopPropagation()} onClick={() => centerOnRoot(viewRef.current.scale)}>
                          Center
                        </button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={4}>
                <Card className="shadow-sm">
                  <CardBody className="skill-tree-detail">
                  <div className="skill-tree-detail-header">
                    <div className="skill-tree-detail-title">Skill Details</div>
                    <div className="skill-tree-detail-meta">
                      <span className="skill-tree-detail-tier">Tier {activeDetailNode.tier}</span>
                      <span className="skill-tree-detail-sep">•</span>
                      <span className="skill-tree-detail-cost">Cost: {activeDetailNode.cost} SP</span>
                    </div>
                  </div>

                  <div className="skill-tree-detail-card">
                    <div className="skill-tree-detail-name">{activeDetailNode.title}</div>
                    <div className="skill-tree-detail-effects">
                      {activeDetailNode.effects.map((e, i) => (
                        <div key={i} className="skill-tree-detail-effect">
                          {e}
                        </div>
                      ))}
                    </div>

                    <div className="skill-tree-detail-purchase">
                      {activeDetailState === "completed" && (
                        <div className="skill-tree-detail-state completed">Completed</div>
                      )}
                      {activeDetailState === "locked" && (
                        <div className="skill-tree-detail-state locked">Locked</div>
                      )}
                      {activeDetailState === "unavailable" && (
                        <div className="skill-tree-detail-state unavailable">Insufficient Skill Points</div>
                      )}
                      {activeDetailState === "unlocked" && (
                        <Button color="primary" onClick={() => handlePurchase(activeDetailNode)}>
                          Unlock ({activeDetailNode.cost} SP)
                        </Button>
                      )}
                    </div>

                    <div className="skill-tree-detail-req">
                      {(activeDetailNode.requires || []).length > 0 ? (
                        <>
                          <div className="skill-tree-detail-req-title">Requires</div>
                          {(activeDetailNode.requires || []).map((r) => {
                            const req = nodeById.get(r);
                            const ok = purchased.has(r);
                            return (
                              <div key={r} className={`skill-tree-detail-req-item ${ok ? "ok" : "no"}`}>
                                {req ? req.title : r}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="skill-tree-detail-req-title">No prerequisites</div>
                      )}
                    </div>
                  </div>

                  <div className="skill-tree-detail-hint">
                    Drag to pan. Pinch or use buttons to zoom. Tap to pin.
                  </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SkillTree;
